/**
 * AcceptInvite — F10 T10.11.
 *
 * Konsumuje zaproszenie z maila — URL `/accept-invite/:token`. Token w path-u.
 * Zaproszony user ustawia haslo (opcjonalnie wyswietlana nazwa) i konto staje
 * sie aktywne. Po sukcesie -> /login z toast'em.
 *
 * tenantSlug wymagane przez backend — czytamy z hosta (`<slug>.app.<base>`),
 * to dziala bo zaproszenie wysylane jest z linkiem na konkretnym tenancie.
 *
 * Anti-enumeration spojnie z backendem: 410 INVALID_OR_EXPIRED dla token-not-found,
 * status != 'invited', expired-czas → wszystko ten sam ekran „Link wygasl lub
 * juz uzyty".
 *
 * Walidacje frontowe: dlugosc >= 10, cyfra, duza litera, powtorz=zgodnie. Backend
 * waliduje policy twardo i zwraca PASSWORD_POLICY_VIOLATION z details[].
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

type State = { kind: 'idle' } | { kind: 'submitting' } | { kind: 'expired' };

interface BackendError {
  error?: string;
  details?: string[] | Record<string, string[]>;
}

function extractError(err: unknown): { status: number | null; data: BackendError | null } {
  const e = err as { response?: { status?: number; data?: BackendError } };
  return {
    status: e.response?.status ?? null,
    data: e.response?.data ?? null,
  };
}

function getTenantSlugFromHost(): string | null {
  if (typeof window === 'undefined') return null;
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  if (parts.length >= 3 && parts[1] === 'app') return parts[0] ?? null;
  return null;
}

function policyErrorsToMessages(details: string[] | undefined, t: (k: string) => string): string[] {
  if (!details) return [];
  return details.map((code) => {
    switch (code) {
      case 'TOO_SHORT':
        return t('auth.acceptInvite.policy.tooShort');
      case 'TOO_LONG':
        return t('auth.acceptInvite.policy.tooLong');
      case 'NO_DIGIT':
        return t('auth.acceptInvite.policy.noDigit');
      case 'NO_UPPER':
        return t('auth.acceptInvite.policy.noUpper');
      case 'NO_LOWER':
        return t('auth.acceptInvite.policy.noLower');
      case 'NO_SPECIAL':
        return t('auth.acceptInvite.policy.noSpecial');
      default:
        return code;
    }
  });
}

export function AcceptInvite() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [policyErrors, setPolicyErrors] = useState<string[]>([]);
  const [mismatchError, setMismatchError] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ kind: 'expired' });
    }
  }, [token]);

  const submitting = state.kind === 'submitting';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPolicyErrors([]);
    setMismatchError(false);

    if (password !== confirm) {
      setMismatchError(true);
      return;
    }

    setState({ kind: 'submitting' });

    try {
      const slug = getTenantSlugFromHost();
      if (!slug) {
        setState({ kind: 'expired' });
        return;
      }
      const body: { password: string; displayName?: string; tenantSlug: string } = {
        password,
        tenantSlug: slug,
      };
      if (displayName.trim()) body.displayName = displayName.trim();

      await api.post(`/users/accept-invite/${token}`, body);
      toast.show({
        variant: 'success',
        title: t('auth.acceptInvite.successTitle'),
        description: t('auth.acceptInvite.successDescription'),
      });
      navigate('/login', { replace: true });
    } catch (err) {
      const { status, data } = extractError(err);
      if (status === 410) {
        setState({ kind: 'expired' });
        return;
      }
      if (status === 400 && data?.error === 'PASSWORD_POLICY_VIOLATION') {
        const details = Array.isArray(data.details) ? data.details : [];
        setPolicyErrors(policyErrorsToMessages(details, t));
        setState({ kind: 'idle' });
        return;
      }
      if (status === 400 && data?.error === 'PASSWORD_RECENTLY_USED') {
        toast.show({
          variant: 'warning',
          title: t('auth.acceptInvite.errors.recentlyUsedTitle'),
          description: t('auth.acceptInvite.errors.recentlyUsedDescription'),
        });
        setState({ kind: 'idle' });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('auth.acceptInvite.errors.genericTitle'),
        description: t('auth.acceptInvite.errors.genericDescription'),
      });
      setState({ kind: 'idle' });
    }
  };

  if (state.kind === 'expired') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
        <div
          className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl"
          data-testid="accept-invite-expired"
        >
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-text-primary">
              {t('auth.acceptInvite.expiredTitle')}
            </h1>
            <p className="mt-3 text-sm text-text-tertiary">
              {t('auth.acceptInvite.expiredDescription')}
            </p>
          </div>
          <div className="mt-8">
            <Link to="/login">
              <Button variant="secondary" className="w-full">
                {t('auth.acceptInvite.backToLogin')}
              </Button>
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
      <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-text-primary">{t('auth.acceptInvite.title')}</h1>
          <p className="mt-2 text-sm text-text-tertiary">{t('auth.acceptInvite.subtitle')}</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Input
            label={t('auth.acceptInvite.displayNameLabel')}
            type="text"
            autoComplete="name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={submitting}
            placeholder={t('auth.acceptInvite.displayNamePlaceholder')}
            data-testid="accept-invite-display-name"
          />
          <Input
            label={t('auth.acceptInvite.passwordLabel')}
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            data-testid="accept-invite-password"
          />
          <Input
            label={t('auth.acceptInvite.confirmLabel')}
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={submitting}
            data-testid="accept-invite-confirm"
          />

          <p className="text-xs text-text-tertiary">{t('auth.acceptInvite.policyHint')}</p>

          {mismatchError && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
              data-testid="accept-invite-mismatch"
            >
              {t('auth.acceptInvite.errors.mismatch')}
            </div>
          )}

          {policyErrors.length > 0 && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
              data-testid="accept-invite-policy-errors"
            >
              <ul className="list-inside list-disc space-y-1">
                {policyErrors.map((msg) => (
                  <li key={msg}>{msg}</li>
                ))}
              </ul>
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            loading={submitting}
            data-testid="accept-invite-submit"
          >
            {submitting ? t('auth.acceptInvite.submitting') : t('auth.acceptInvite.submit')}
          </Button>
        </form>
      </div>
    </main>
  );
}
